// UI mirror of the FROZEN seams `operator-bff:WS OperatorAlert@v1` and
// `operator-bff:GET /v1/alerts@v1` (docs/verticals/operator-notifications.md §7.4) —
// verbatim; do not re-derive or rename a field here. The BFF routes one alert to every
// `alerts:airport:<IATA>` room it touches (scope is enforced by the SERVER: this console
// never receives another airport's news, so it never filters any out — brief §6 inv. 1).
//
// Envelope: `{ type: 'OperatorAlert', payload: IOperatorAlertV1 }` — the same shape as
// `OperatorNotification` (lib/ws/apply-event.ts); `socket.ts` rebuilds it from the
// event name + body.

export type OperatorAlertKind =
  | 'FLIGHT_DELAYED'
  | 'FLIGHT_DELAY_CHANGED'
  | 'FLIGHT_CANCELLED'
  | 'FLIGHT_DIVERTED'
  | 'DISRUPTION_OPENED';

export interface IOperatorAlertV1 {
  /** = the event `idempotency_key` — the dedupe key end to end (brief §6 inv. 4). */
  id: string;
  kind: OperatorAlertKind;
  severity: 'info' | 'warning' | 'critical';
  /** The IATA rooms it was routed to. */
  airports: string[];
  /** observedAt (ISO-8601). */
  at: string;
  flight?: {
    urn: string;
    number: string;
    origin: string;
    destination: string;
    std: string;
    etd?: string;
    delayMinutes?: number;
  };
  disruption?: {
    urn: string;
    /** The cases auto-label («SKY — MECHANICAL disruption») — shown only when an operator wrote it. */
    title: string;
    status: 'PREDICTED' | 'ACTIVE';
    flightCount: number;
    /** Additive-optional: the cases `DisruptionReason` code, verbatim (`MECHANICAL`…). */
    reason?: string;
  };
  source: 'AIRLINE' | 'OPERATOR' | 'PREDICTOR';
  /** Set when an operator caused it — that operator is never alerted (§7.5). */
  actorUrn?: string;
}

/** `GET /v1/alerts?since=<iso>&limit=<n>` → this. */
export interface IAlertsPage {
  items: IOperatorAlertV1[];
  unread: number;
  readUpTo: string | null;
}

export interface OperatorAlertEvent {
  type: 'OperatorAlert';
  payload: IOperatorAlertV1;
}

/**
 * Shape-check at the door: only what the console keys on or orders by is required. `kind`
 * is checked as a string, not against the five known kinds, so a newer BFF's kind still
 * arrives intact and the delivery policy skips it explicitly (`deliveryOf` → 'none')
 * instead of it being dropped here as "malformed". An optional block, when present, must
 * carry the URN the console links and relates by.
 */
export function isOperatorAlertV1(value: unknown): value is IOperatorAlertV1 {
  const a = value as Partial<IOperatorAlertV1> | null;
  return (
    typeof a?.id === 'string' &&
    typeof a.kind === 'string' &&
    !Number.isNaN(Date.parse(a.at ?? '')) &&
    Array.isArray(a.airports) &&
    (a.flight === undefined || typeof a.flight?.urn === 'string') &&
    (a.disruption === undefined || typeof a.disruption?.urn === 'string')
  );
}
